import React, { useEffect } from 'react';
import { useRouteMatch, generatePath, Route, Switch } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { Box, Typography } from '@mui/material';
import { DateRange as DateRangeIcon, FilterList as FilterListIcon } from '@mui/icons-material';
import { assign } from 'lodash';
import routes from '../../../../routes';
import RescuerScheduleMobileWithInfiniteScroll from './rescuerSchedule/RescuerScheduleMobileWithInfiniteScroll';
import RescueMobileCard from '../../../Rescue/RescueMobileCard';
import useActiveUser from '../../../../hooks/useActiveUser';
import { getScheduleAvailableRescues } from '../../../../helpers/RescuesHelper';
import MobileWrapper from '../../../../components/Common/MobileBottomNavWrapper';
import { initialRescuerScheduleFilters } from '../../../../reducers/ui';
import { RESCUER_SCHEDULE_FILTER } from '../Schedule/ScheduleMobile';
import useUIFilter from '../../../../hooks/useUIFilter';
import { DISTANCE_ALL_VALUE } from '../../../../components/RescuersScheduleFilters/filters/DistanceSliderFilter';
import RescuerScheduleApplyFilters from './rescuerScheduleFilters/RescuerScheduleApplyFilters';
import ClaimRescueBreadcrumb from './Components/ClaimRescueBreadcrumb';
import useHasActiveFeatureFlag from '../../../../hooks/useFeatureFlags';
import { FF_REQUIRE_FOOD_SAFETY_COURSE_COMPLETION } from '../../../../components/FeatureFlagWrapper';
import FoodSafetyCourseConfirmationDialog from '../../../../components/FoodSafetyCourseConfirmationDialog';
import useRescuerDashboardActions from '../../../../hooks/useRescuerDashboardActions';

export const RESCUER_MOBILE_DASHBOARD_TAB_SCHEDULE = 'schedule';
export const RESCUER_MOBILE_DASHBOARD_TAB_SCHEDULE_V2 = 'schedule-v2';
export const RESCUER_MOBILE_DASHBOARD_TAB_FILTERS = 'filters';

export const paths = [
  generatePath(routes.mobileDashboardClaimRescue, { tab: RESCUER_MOBILE_DASHBOARD_TAB_SCHEDULE }),
  generatePath(routes.mobileDashboardClaimRescue, { tab: RESCUER_MOBILE_DASHBOARD_TAB_FILTERS }),
  generatePath(routes.mobileDashboardClaimRescue, { tab: RESCUER_MOBILE_DASHBOARD_TAB_SCHEDULE_V2 }),
];

export function RescuesLoader({ highlighted, rows }) {
  return (
    <Box display="grid" rowGap={2} width="100%">
      {[...Array(rows || 10).keys()].map((index) => (
        <RescueMobileCard key={index} highlighted={highlighted} isLoading />
      ))}
    </Box>
  );
}

export function NoRescueCard({ text }) {
  return (
    <Box
      boxShadow={25}
      p={2}
      sx={{
        width: '100%',
        borderRadius: 16,
        backgroundColor: '#fff',
        position: 'relative',
      }}
    >
      <Typography color="textSecondary" variant="body1">
        {text}
      </Typography>
    </Box>
  );
}

const RescuerClaimRescueDashboardMobile = () => {
  const { url } = useRouteMatch();
  const activeUser = useActiveUser();
  const rescuesEntities = useSelector((state) => state.entities.rescues);
  const featureFlagsEntities = useSelector((state) => state.entities.featureFlags);
  const { getUIFilter } = useUIFilter();
  const scheduleFilters = assign(initialRescuerScheduleFilters, getUIFilter(RESCUER_SCHEDULE_FILTER));
  const showInitialInfiniteScrollTour = scheduleFilters.showInitialInfiniteScrollTour.value;
  const { formatMessage } = useIntl();
  const { fetchScheduleTabPaginated } = useRescuerDashboardActions();
  const coursesEntities = useSelector((state) => state.entities.users.courses);
  const courses = coursesEntities.byUserId[activeUser.id] || [];
  const coursesCompleted =
    activeUser?.courses_status === 'Completed' ||
    (courses.length > 0 &&
      courses.every((course) =>
        course?.modules.length > 0 ? course.modules.every((module) => module.completed) : false
      ));
  const hideFoodSafetyDialog = useSelector(state => state.ui.persisted.hideFoodSafetyDialog);
  const hasActiveRequireFoodSafetyFF = useHasActiveFeatureFlag(FF_REQUIRE_FOOD_SAFETY_COURSE_COMPLETION);
  const showFoodSafetyDialog =
    !coursesCompleted && (hasActiveRequireFoodSafetyFF || !hideFoodSafetyDialog) && !showInitialInfiniteScrollTour;

  useEffect(() => {
    fetchScheduleTabPaginated(true, true, 1, true);
  }, [activeUser.id, fetchScheduleTabPaginated]);

  const activeFiltersCount = Object.keys(scheduleFilters).reduce((acc, filterKey) => {
    const filterValue = scheduleFilters[filterKey].value;
    let countWeight = 0;
    switch (filterKey) {
      case 'showOnlyAvailableRescues':
        countWeight = filterValue === false ? 0 : 1;
        break;
      case 'days':
      case 'rescueSizes':
      case 'donorReceiverList':
      case 'query':
      case 'siteIds':
      case 'pickupCities':
      case 'dropoffCities':
        countWeight = filterValue.length === 0 ? 0 : 1;
        break;
      case 'distance':
        countWeight = filterValue === DISTANCE_ALL_VALUE ? 0 : 1; // 50
        break;
      case 'donorLink':
      case 'after':
      case 'before':
        countWeight = filterValue ? 1 : 0;
        break;
    }
    return acc + countWeight;
  }, 0);

  const availableRescues = getScheduleAvailableRescues(activeUser.id, rescuesEntities);
  const scheduleCount = availableRescues.data.length;

  const getActiveTab = () => {
    const path = paths.indexOf(url);

    return Math.max(0, path); // no negatives numbers
  };

  if (featureFlagsEntities.inflight) {
    return null;
  }

  const bottomTabs = [
    {
      label: formatMessage({ id: 'rescuer-claim-rescue-mobile.bottom-navigation.schedule' }),
      url: paths[0],
      icon: <DateRangeIcon sx={{ fontSize: '1.8rem' }} />,
      badgeCounter: scheduleCount,
      testId: 'rescuerDesktopTab0',
    },
    {
      label: formatMessage({ id: 'rescuer-claim-rescue-mobile.bottom-navigation.schedule-filters' }),
      url: paths[1],
      icon: <FilterListIcon sx={{fontSize: '1.8rem',}} />,
      badgeCounter: activeFiltersCount,
      testId: 'schedule-filters',
    },
  ];

  return (
    <>
      {showFoodSafetyDialog && <FoodSafetyCourseConfirmationDialog isMobile />}

      <MobileWrapper
        containerId="rescuer-dashboard-mobile-div"
        breadcrumb={<ClaimRescueBreadcrumb />}
        activeTab={getActiveTab()}
        bottomTabs={bottomTabs}
        pageContent={
          <Switch>
            <Route
              path={[generatePath(routes.mobileDashboardClaimRescue), paths[0]]}
              exact
              component={RescuerScheduleMobileWithInfiniteScroll}
            />
            <Route path={paths[1]} exact component={RescuerScheduleApplyFilters} />
          </Switch>
        }
      />
    </>
  );
};

export default RescuerClaimRescueDashboardMobile;
